import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { staggerContainer, fadeInUp } from "./AnimationVariants";
import dashboardShot from "../../assets/homepage/product-dashboard.webp";
import phoneShot from "../../assets/homepage/product-phone-classes.webp";
import membershipShot from "../../assets/homepage/product-phone-membership.webp";

// Same left-to-right order as the pictures: classes phone, dashboard, membership phone
const platforms = [
  {
    title: "iOS and Android app",
    text: "For athletes and coaches: book classes, see the workout, log results.",
  },
  {
    title: "Web dashboard",
    text: "For owners and coaches: schedule, members, programming and payments.",
  },
  {
    title: "Memberships",
    text: "Members see their plan and pay in the app; the gym gets paid automatically.",
  },
];

export function ProductShowcase() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} aria-labelledby="product-title" className="w-full py-14 md:py-16">
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="mx-auto max-w-[1400px] w-[calc(100%-2rem)] md:w-[calc(100%-240px)] text-center"
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs leading-none uppercase tracking-[0.12em] text-white/55"
        >
          The product
        </motion.p>
        <motion.h2
          id="product-title"
          variants={fadeInUp}
          className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight text-white"
        >
          One product, two screens.
        </motion.h2>
        <motion.p variants={fadeInUp} className="mt-3 text-base text-white/60">
          The web dashboard runs the gym. The mobile app is where everyone trains.
        </motion.p>

        {/* Capped width keeps the pictures, heading and captions on one screen */}
        <div className="mx-auto w-full max-w-[1000px]">
          {/* Phones overlap the dashboard's lower corners; bottom padding leaves room for them */}
          <motion.div
            variants={fadeInUp}
            className="relative mt-10 md:mt-12 pb-10 sm:pb-14 md:px-[9%]"
          >
            <img
              src={dashboardShot}
              alt="Lemonade Business dashboard: a week of classes in lanes, each with its coach, room and bookings"
              width={1894}
              height={1052}
              loading="lazy"
              className="w-full h-auto rounded-xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            />
            <img
              src={phoneShot}
              alt="Lemonade app: today's classes with a full class and its waitlist, a booked class and a waitlist spot"
              width={603}
              height={1311}
              loading="lazy"
              className="absolute bottom-0 left-0 w-[18%] h-auto rounded-[1.75rem] border border-white/20 shadow-[0_30px_80px_rgba(0,0,0,0.8)]"
            />
            <img
              src={membershipShot}
              alt="Lemonade app: a member's 12-month membership with the monthly price, payments left and next charge"
              width={603}
              height={1311}
              loading="lazy"
              className="absolute bottom-0 right-0 w-[18%] h-auto rounded-[1.75rem] border border-white/20 shadow-[0_30px_80px_rgba(0,0,0,0.8)]"
            />
          </motion.div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[18%_1fr_18%] gap-6 lg:gap-8 text-center lg:text-left">
            {platforms.map((p) => (
              <motion.div key={p.title} variants={fadeInUp}>
                <h3 className="text-base font-semibold text-white">{p.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/60">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
